// @system — "Report a problem": the box inside the Help panel a user of THIS app types into when a page
// is broken. POSTs to the app's own /api/problem-reports (api/@system/problem-reports), which stores the
// report and tells the APP OWNER (e-mail, in-app bell, their Orkosi dashboard). Nothing leaves for the
// platform's bug queue. Anonymous by design: the optional e-mail is how the owner can reply.

import { useState } from 'react'
import { AlertTriangle, Send, CheckCircle2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input, Textarea } from '../Form'
import { api } from '@/app/lib/@system/api'

/**
 * @typedef {object} ReportProblemFormProps
 * @property {() => void} [onBack] - Return to the help articles
 */

/**
 * ReportProblemForm — one text box, an optional e-mail, Send.
 * @param {ReportProblemFormProps} props
 */
export function ReportProblemForm({ onBack }) {
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [sentId, setSentId] = useState(/** @type {number | null} */ (null))

  /** @param {import('react').FormEvent<HTMLFormElement>} e */
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!message.trim()) {
      setError('Tell us what went wrong')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const body = {
        message: message.trim(),
        email: email.trim() || undefined,
        page_url: typeof window !== 'undefined' ? window.location.href.slice(0, 2048) : undefined,
        page_title: typeof document !== 'undefined' ? document.title.slice(0, 400) : undefined,
      }
      const res = await api.post('/problem-reports', body)
      setSentId(typeof res?.id === 'number' ? res.id : 0)
      setMessage('')
      setEmail('')
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'The report could not be sent. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (sentId !== null) {
    return (
      <div className="p-4 text-center" data-testid="problem-report-sent">
        <CheckCircle2 className="h-10 w-10 text-[var(--color-success)] mx-auto mb-2" aria-hidden="true" />
        <p className="text-sm font-medium">Thank you. The team behind this app has your report.</p>
        <p className="text-xs text-brand-text-muted mt-1">
          {sentId ? `Report #${sentId}. ` : ''}They see it right away and can reply if you left an e-mail.
        </p>
        <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => setSentId(null)}>
          Report another problem
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-3" data-testid="problem-report-form" noValidate>
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-[var(--color-warning)] shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-brand-text-muted">
          Something broken on this page? Describe it and the people who run this app are told right away.
        </p>
      </div>
      <div>
        <label htmlFor="problem-report-message" className="block text-sm font-medium mb-1">
          What went wrong?
        </label>
        <Textarea
          id="problem-report-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What did you try, what happened instead?"
          rows={4}
          maxLength={5000}
          required
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? 'problem-report-error' : undefined}
        />
      </div>
      <div>
        <label htmlFor="problem-report-email" className="block text-sm font-medium mb-1">
          Your e-mail <span className="text-brand-text-muted font-normal">(optional, so they can reply)</span>
        </label>
        <Input
          id="problem-report-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          className="h-11 sm:h-10"
        />
      </div>
      {error ? (
        <p id="problem-report-error" role="alert" className="text-sm text-[var(--color-error)]">
          {error}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-2 pt-1">
        {onBack ? (
          <Button type="button" variant="ghost" size="sm" onClick={onBack}>
            Back to help
          </Button>
        ) : (
          <span />
        )}
        <Button type="submit" size="sm" disabled={submitting} className="gap-2">
          <Send className="h-4 w-4" aria-hidden="true" />
          {submitting ? 'Sending…' : 'Send report'}
        </Button>
      </div>
    </form>
  )
}
